/**
 * Branch picker list shared by the cart's Change Item and Manage Order sheets
 * (DEV-51046, AC5).
 */

/** Branches shown in a picker before the one in use is guaranteed a place. */
export const NEAREST_BRANCH_LIMIT = 3

interface PickableBranch {
  branchId: string
  distanceMiles: number | null
}

/** Sort comparator: nearest first, unknown distances last. */
export function byDistanceNearestFirst(a: PickableBranch, b: PickableBranch): number {
  return (a.distanceMiles ?? Infinity) - (b.distanceMiles ?? Infinity)
}

/**
 * The branches to offer when changing where a line comes from: the nearest
 * few to the engineer's current location, plus the branch the line is at
 * when it isn't among them — so after a move they can still see what they'd
 * be leaving. Anchored on the branch in use rather than the picker's current
 * pick, so trying another branch doesn't drop it from the list. Ordered
 * nearest first.
 *
 * From BL1 1SA with a Wolseley Stockport line that reads: Bolton, Bury,
 * Trafford Park, then Stockport.
 */
export function nearestBranchOptions<T extends PickableBranch>(
  branches: T[],
  inUseBranchId: string | null,
  limit: number = NEAREST_BRANCH_LIMIT,
): T[] {
  const sorted = [...branches].sort(byDistanceNearestFirst)
  const nearest = sorted.slice(0, limit)

  if (inUseBranchId == null) return nearest
  if (nearest.some((branch) => branch.branchId === inUseBranchId)) return nearest

  const inUse = sorted.find((branch) => branch.branchId === inUseBranchId)
  return inUse ? [...nearest, inUse] : nearest
}
